import React, { useState, useRef, useCallback } from 'react';

/* ─── Image resolver: maps product to configurator PNG ─── */
export const getModuleImage = (product) => {
  const img = (product.image || product._image || '').toLowerCase();
  if (img === 'vacwayone')  return '/images/configurator/VACWAYone.png';
  if (img === 'vacwaymini') return '/images/configurator/VACWAYmini.png';
  if (img === 'vacwaygo')   return '/images/configurator/VACWAYgo.png';
  if (img === 'vacwaylay')  return '/images/configurator/VACWAYlay.png';
  if (img === 'vacwaypack') return '/images/configurator/VACWAYpack.png';
  if (img.startsWith('/') || img.startsWith('http')) return img;
  // Fallback by name
  const name = (product.name || '').toLowerCase();
  if (name.includes('mini')) return '/images/configurator/VACWAYmini.png';
  return '/images/configurator/VACWAYone.png';
};

export const getModuleWidth = (_product, _env) => 120;

/* ─── Bottom offset: all machines sit on the floor ─── */
export const getModuleBottomOffset = (_product) => 0;

export const getModuleImageHeight = (product) => {
  const img = (product.image || product._image || '').toLowerCase();
  const name = (product.name || '').toLowerCase();
  if (img === 'vacwaymini' || name.includes('mini')) return 160;
  return 200; // VACWAYone default
};

/* ─── Unique ID helper ─── */
let _uid = 0;
const uid = () => `vll-${Date.now()}-${++_uid}`;

/* ═══════════════════════════════════════════════════
   VisualLockerLine — Controlled component
   Props:
     products      – DB products (cabinet)
     env           – 'interior' | 'exterior'
     lockedEnv     – 'interior' | 'exterior' | null
     configLine    – array of placed modules (parent-managed)
     onChange(line) – called on every line change
   ═══════════════════════════════════════════════════ */
export default function VisualLockerLine({ products = [], env = 'interior', lockedEnv = null, configLine = [], onChange, consumableItems = [] }) {
  const [draggedCatalog, setDraggedCatalog] = useState(null);
  const [draggedIndex,   setDraggedIndex]   = useState(null);
  const [dragOverIndex,  setDragOverIndex]  = useState(null);
  const dropZoneRef = useRef(null);

  const push = (newLine) => { if (onChange) onChange(newLine); };

  /* ─── Catalog sidebar ─── */
  const catalog = products.filter(p => {
    if (!p) return false;
    return p.category === 'cabinet' || p.category === 'pos';
  });

  const addModule = (prod) => {
    push([...configLine, { ...prod, uid: uid(), _imgWidth: getModuleWidth(prod) }]);
  };

  /* ─── Drag from catalog ─── */
  const onCatalogDragStart = (e, prod) => {
    setDraggedCatalog(prod);
    e.dataTransfer.effectAllowed = 'copy';
  };

  /* ─── Drop ─── */
  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (draggedCatalog) {
      const idx = dragOverIndex != null ? dragOverIndex : configLine.length;
      const next = [...configLine];
      next.splice(idx, 0, { ...draggedCatalog, uid: uid(), _imgWidth: getModuleWidth(draggedCatalog) });
      push(next);
      setDraggedCatalog(null);
      setDragOverIndex(null);
      return;
    }
    if (draggedIndex != null && dragOverIndex != null && draggedIndex !== dragOverIndex) {
      const next = [...configLine];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(dragOverIndex > draggedIndex ? dragOverIndex - 1 : dragOverIndex, 0, moved);
      push(next);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedCatalog, draggedIndex, dragOverIndex, configLine, env]);

  /* ─── Reorder within line ─── */
  const onLineDragStart = (e, idx) => { setDraggedIndex(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onLineDragOver  = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const onLineDragEnd   = ()       => { setDraggedIndex(null); setDragOverIndex(null); };
  const removeModule    = (uid)    => push(configLine.filter(m => m.uid !== uid));

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

      {/* ══ Drop zone ══ */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Drop zone */}
          <div
            ref={dropZoneRef}
            style={{
              flex: 1,
              minHeight: 380,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              background: 'linear-gradient(180deg, #f8fbff 0%, #edf3fa 100%)',
              border: `2.5px dashed ${draggedCatalog ? '#0b97ff' : '#d0d8e2'}`,
              borderRadius: 10,
              padding: configLine.length === 0 ? '40px 24px' : '8px 24px 0 24px',
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'hidden',
              cursor: 'copy',
              transition: 'border-color 0.2s, background 0.2s',
              boxShadow: draggedCatalog ? 'inset 0 0 24px rgba(11,151,255,0.06)' : 'none',
            }}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = draggedCatalog ? 'copy' : 'move'; }}
            onDragLeave={() => setDragOverIndex(null)}
          >
            {/* Empty placeholder */}
            {configLine.length === 0 && consumableItems.length === 0 && (
              <div style={{ textAlign: 'center', color: '#b0b8c4', userSelect: 'none', pointerEvents: 'none' }}>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"
                  style={{ opacity: 0.35, display: 'block', margin: '0 auto 10px' }}>
                  <rect x="2" y="3" width="5" height="18" rx="1"/>
                  <rect x="7" y="3" width="5" height="18" rx="0"/>
                  <rect x="12" y="3" width="5" height="18" rx="0"/>
                  <rect x="17" y="3" width="5" height="18" rx="1"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Arrastra máquinas aquí</div>
                <div style={{ fontSize: 12, color: '#c8cdd4', marginTop: 4 }}>o haz clic en "+" del panel lateral</div>
              </div>
            )}

            {/* Floor line */}
            {(configLine.length > 0 || consumableItems.length > 0) && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                background: 'linear-gradient(90deg, #c4cdd8, #a0adb8, #c4cdd8)', borderRadius: '2px 2px 0 0', zIndex: 1 }} />
            )}

            {/* Grouped machines */}
            {(() => {
              const groups = {};
              configLine.forEach(m => {
                const key = (m.image || m._image || m.name || 'unknown').toLowerCase();
                if (!groups[key]) groups[key] = { machine: m, key, uids: [] };
                groups[key].uids.push(m.uid);
              });
              return Object.values(groups).map(({ machine: m, key, uids }, gIdx) => {
                const count = uids.length;
                const tag = (m.code || m.name || '').replace(/^(POS_)?(INT_|EXT_)?/, '').split(/[\s-]/)[0];
                const removeOne = () => {
                  const lastUid = uids[uids.length - 1];
                  push(configLine.filter(c => c.uid !== lastUid));
                };
                return (
                  <div
                    key={gIdx}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(gIdx); }}
                    onDragEnd={onLineDragEnd}
                    style={{
                      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                      position: 'relative', flexShrink: 0, zIndex: 2,
                      margin: 0, padding: 0, lineHeight: 0, verticalAlign: 'bottom',
                    }}
                    className="vll-module"
                  >
                    {/* Quantity badge */}
                    <div style={{
                      position: 'absolute', top: 6, right: 6,
                      background: '#022040', color: '#fff', borderRadius: 14,
                      minWidth: 28, padding: '3px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, zIndex: 10, whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)', lineHeight: 1,
                    }}>×{count}</div>

                    <img
                      src={getModuleImage(m)}
                      alt={m.name}
                      style={{
                        height: Math.round(getModuleImageHeight(m) * 1.6), width: 'auto',
                        objectFit: 'contain', display: 'block', margin: 0, flexShrink: 0,
                        padding: 0, marginBottom: getModuleBottomOffset(m),
                      }}
                    />
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#022040', textAlign: 'center',
                      whiteSpace: 'nowrap', zIndex: 3, lineHeight: 'normal',
                      margin: 0, padding: '2px 0 8px',
                    }}>
                      {tag}
                    </span>

                    {/* Remove one button */}
                    <button
                      onClick={removeOne}
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 22, height: 22, borderRadius: '50%', border: 'none',
                        background: 'rgba(239,68,68,0.88)', color: '#fff',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 0, zIndex: 11, opacity: 0, transition: 'opacity 0.15s',
                      }}
                      className="vll-remove"
                      title="Eliminar uno"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                );
              });
            })()}

            {/* Consumables */}
            {consumableItems.map((c, idx) => (
              <div key={`cons-${idx}`} style={{
                display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                position: 'relative', flexShrink: 0, zIndex: 2,
                margin: '0 8px', padding: 0, lineHeight: 0, verticalAlign: 'bottom',
              }}>
                {/* Quantity badge */}
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  background: '#b45309', color: '#fff', borderRadius: 14,
                  minWidth: 28, padding: '3px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, zIndex: 10, whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)', lineHeight: 1,
                }}>×{c.qty}</div>
                {c.image ? (
                  <img src={getModuleImage(c)} alt={c.name} style={{ height: 150, width: 150, objectFit: 'contain', display: 'block' }} />
                ) : (
                  <div style={{
                    width: 150, height: 150,
                    background: '#fffbf0', border: '2px solid #fcd34d', borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    marginBottom: 0,
                  }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="1.4">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                      <line x1="12" y1="22.08" x2="12" y2="12"/>
                    </svg>
                  </div>
                )}
                <span style={{
                  fontSize: 9, fontWeight: 700, color: '#022040', textAlign: 'center',
                  lineHeight: 1.2, margin: 0, padding: '4px 0 8px', maxWidth: 150, whiteSpace: 'normal',
                }}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>

        {configLine.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => push([])}
              style={{ padding: '4px 14px', fontSize: 12, border: '1.5px solid #e0e0e0', borderRadius: 6, background: '#fff', color: '#999', cursor: 'pointer', fontWeight: 600 }}
            >
              Limpiar linea
            </button>
          </div>
        )}
      </div>

      {/* ══ Sidebar catalog ══ */}
      <div style={{
        width: 230, flexShrink: 0,
        background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0',
        padding: 14, maxHeight: 520, overflowY: 'auto',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 10 }}>
          Máquinas disponibles
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {catalog.map(prod => {
            const shortName = (prod.name || '')
              .replace(/^(INTERIOR|EXTERIOR)\s*-\s*/i, '');
            return (
            <div
              key={prod._id || prod.code}
              draggable
              onDragStart={(e) => onCatalogDragStart(e, prod)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 8px', border: '1.5px solid #e2e8f0', borderRadius: 8,
                background: '#fafbfc', cursor: 'grab',
                transition: 'border-color 0.15s',
              }}
            >
              <img
                src={getModuleImage(prod)}
                alt={prod.name}
                style={{ width: 38, height: 48, objectFit: 'contain', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {shortName}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{prod.code}</div>
              </div>
              <button
                onClick={() => addModule(prod)}
                style={{
                  width: 26, height: 26, borderRadius: '50%', border: 'none',
                  background: '#022040', color: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
                title="Añadir"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
            );
          })}
          {catalog.length === 0 && (
            <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
              Sin productos disponibles
            </div>
          )}
        </div>
      </div>

      {/* Hover CSS for module remove button */}
      <style>{`
        .vll-module:hover .vll-remove { opacity: 1 !important; }
        .vll-module:hover { transform: translateY(-3px); }
      `}</style>
    </div>
  );
}
